// File: src/components/research/ResearchHistoryDialog.tsx

import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import {Close as CloseIcon, Visibility as ViewIcon} from '@mui/icons-material';
import {Product, ResearchIteration} from '../../types';

interface ResearchHistoryDialogProps {
    open: boolean;
    onClose: () => void;
    product: Product | undefined;
    historyQuery: {
        data?: ResearchIteration[];
        isLoading: boolean;
        isError: boolean;
    };
    onViewResearch?: (researchId: number) => void;
}

const ResearchHistoryDialog: React.FC<ResearchHistoryDialogProps> = ({
                                                                         open,
                                                                         onClose,
                                                                         product,
                                                                         historyQuery,
                                                                         onViewResearch
                                                                     }) => {
    // Debug effect for tracking caching issues
    React.useEffect(() => {
        if (open && product) {
            console.log(`ResearchHistoryDialog opened for product:`, product.id, product.name);
            console.log(`History query data:`, historyQuery.data?.length || 0, 'items');
        }
    }, [open, product, historyQuery.data]);

    const handleViewClick = (researchId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        onViewResearch?.(researchId);
    };

    const handleRowClick = (researchId: number) => {
        onViewResearch?.(researchId);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box>
                    <Typography variant="h6" component="h2">
                        Research History
                    </Typography>
                    {product && (
                        <Typography variant="subtitle2" color="text.secondary">
                            Product: {product.name}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {historyQuery.isLoading && (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                        <CircularProgress/>
                    </Box>
                )}

                {historyQuery.isError && (
                    <Alert severity="error" sx={{mb: 2}}>
                        Error loading research history
                    </Alert>
                )}

                {historyQuery.data && historyQuery.data.length === 0 && (
                    <Box sx={{textAlign: 'center', py: 4}}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Research history is empty
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Run the first research for this product
                        </Typography>
                    </Box>
                )}

                {historyQuery.data && historyQuery.data.length > 0 && (
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Iteration</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>AI Service</TableCell>
                                    <TableCell>Created Date</TableCell>
                                    <TableCell>Cost</TableCell>
                                    <TableCell width={100}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyQuery.data.map((gen) => (
                                    <TableRow
                                        key={gen.id}
                                        hover
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                        onClick={() => handleRowClick(gen.id)}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{fontWeight: 500}}>
                                                #{gen.iteration_number}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    gen.status === 'completed' ? 'Completed' :
                                                        gen.status === 'pending' ? 'Pending' :
                                                            gen.status === 'processing' ? 'Processing' :
                                                                gen.status === 'failed' ? 'Failed' :
                                                                    gen.status
                                                }
                                                color={
                                                    gen.status === 'completed' ? 'success' :
                                                        gen.status === 'failed' ? 'error' :
                                                            'warning'
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{gen.ai_service_used}</TableCell>
                                        <TableCell>{new Date(gen.created_at).toLocaleString()}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                ${gen.research_cost?.amount_usd?.toFixed(4) || '0.0000'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="View Results">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleViewClick(gen.id, e)}
                                                    disabled={gen.status !== 'completed'}
                                                >
                                                    <ViewIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ResearchHistoryDialog;
